import { format, isValid, parseISO } from 'date-fns';
import type { PillTone } from '@/components/common/StatusPill';
import type { TimelineStep } from '@/components/common/Timeline';
import type { ProductCase } from '@/types/product';

/** Which shipment screen to show: S1 in transit, S2 delivery failed, S3 not shipped, S4 delivered. */
export type ShipmentView = 'in_transit' | 'failed' | 'not_shipped' | 'delivered';

export const shipmentPath = (orderId: string) => `/customer/orders/${orderId}/shipment`;

export function shipmentView(order: ProductCase): ShipmentView {
  if (order.state !== 'on_the_way') return 'delivered';
  if (order.shipment?.failedAttempt) return 'failed';
  if (!order.shipment || (order.shipment.stage === 'Ordered' && !order.shipment.awb)) return 'not_shipped';
  return 'in_transit';
}

export const SHIPMENT_BADGE: Record<ShipmentView, { tone: PillTone; label: string }> = {
  in_transit: { tone: 'info', label: 'In transit' },
  failed: { tone: 'warning', label: 'Delivery failed' },
  not_shipped: { tone: 'neutral', label: 'Being packed' },
  delivered: { tone: 'success', label: 'Delivered' },
};

/** "30 Oct, 11:05 AM" (or "30 Oct 2026, 11:05 AM" with `withYear`); no time when the value has none. */
export const formatEventTime = (value?: string, { withYear = false } = {}) => {
  if (!value) return undefined;
  const d = parseISO(value);
  if (!isValid(d)) return undefined;
  const day = withYear ? 'dd MMM yyyy' : 'dd MMM';
  return format(d, value.includes('T') ? `${day}, h:mm a` : day);
};

/**
 * Progress steps for the shipment: the courier's scan history when the API sends it,
 * otherwise the standard steps up to the order's current stage.
 */
export function shipmentSteps(order: ProductCase): TimelineStep[] {
  if (order.shipment?.events?.length) {
    const steps = [...order.shipment.events];
    if (order.state === 'on_the_way' && !steps.some((s) => /deliver/i.test(s.title) && s.state === 'pending')) {
      steps.push({ title: 'Delivered', state: 'pending' });
    }
    return steps;
  }

  const store = order.merchant ?? 'The store';
  const stage = order.state === 'on_the_way' ? order.shipment?.stage ?? 'Ordered' : 'Delivered';
  // Index of the step in progress; everything before it is done.
  const current = { Ordered: 1, Shipped: 2, 'In transit': 3, Delivered: 6 }[stage];
  const lastEvent = order.shipment?.detail?.split(' · ').slice(1).join(' · ') || undefined;

  const steps: { title: string; doneTitle?: string; meta?: string; currentMeta?: string }[] = [
    { title: 'Order placed', meta: formatEventTime(order.orderedAt) },
    { title: 'Packing', doneTitle: order.merchant ? `Packed by ${order.merchant}` : 'Packed', currentMeta: `${store} is packing your order` },
    { title: 'Shipped' },
    { title: 'In transit', currentMeta: lastEvent },
    { title: 'Out for delivery' },
    { title: 'Delivered', meta: formatEventTime(order.deliveredAt) },
  ];

  return steps.map((step, i) => {
    const state: TimelineStep['state'] = i < current ? 'done' : i === current ? 'current' : 'pending';
    return {
      title: state === 'done' ? step.doneTitle ?? step.title : step.title,
      meta: state === 'current' ? step.currentMeta ?? step.meta : state === 'done' ? step.meta : undefined,
      state,
    };
  });
}
